/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraControllerContextIdentifier,
  GamoraFoundationGlueListenerType,
  GamoraFoundationNodeArgs,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakClientStateInvoker,
  HoruzProtocolChulakClientStateInvokerAppTagsRefreshedInput,
  HoruzProtocolChulakClientStateInvokerStartInput,
} from './enums.js';
import {
  HoruzProtocolChulakMessageAgentStartCompletedGuard,
  HoruzProtocolChulakMessageAgentStartExitGuard,
  HoruzProtocolChulakMessageAppPairingEnterGuard,
} from '../../guards/message/index.js';
import { HoruzProtocolChulakClientStateBase } from './base.js';
import { HoruzProtocolChulakClientStateContext } from '../context.js';
import { HoruzProtocolChulakClientStateMap } from '../../statemap.js';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakErrorProtocolInvalidMessage } from '../../errors/protocol/invalid-message.error.js';
import { HoruzProtocolChulakMessageAppTagsRefreshedGuardInput } from '../../guards/message/app-tags-refreshed.js';
import { IHoruz } from '../../../../horuz.js';
const debug = debugCreate({
  namespace: 'horuz:protocols:chulak:states:client:agent-start',
});

/**
 * Client
 *
 * Initialization state
 * @public
 */
export class HoruzProtocolChulakClientStateAgentStart extends HoruzProtocolChulakClientStateBase<
  typeof HoruzProtocolChulakClientStateMap,
  HoruzProtocolChulakClientStateContext,
  IHoruz
> {
  constructor(args: GamoraFoundationNodeArgs) {
    super(args);

    this._onAgentStartCompletedMessage =
      this._onAgentStartCompletedMessage.bind(this);
    this._onAppPairingEnterMessage = this._onAppPairingEnterMessage.bind(this);
    this._onAgentStartExitMessage = this._onAgentStartExitMessage.bind(this);
  }

  /**
   * Event on message
   * @param contextIdentifier The context identifier
   */
  private _onQueueNext = async (
    clientStateContext: HoruzProtocolChulakClientStateContext,
  ) => {
    return this.$tryPostTask(clientStateContext, async () => {
      if (this.$hasPostedTransition(clientStateContext)) {
        return;
      }

      try {
        // Execute state
        const result = this.recv(clientStateContext);
        if (result.done === false) {
          // Decode message type
          switch (result.message.type) {
            case HoruzProtocolChulakDataType.DATA_AGENT_START_COMPLETED: {
              await this._onAgentStartCompletedMessage(
                clientStateContext,
                result.message,
              );
              break;
            }
            case HoruzProtocolChulakDataType.DATA_APP_TAGS_REFRESHED: {
              await this._onAppTagsRefreshed(
                clientStateContext,
                result.message,
              );
              break;
            }
            case HoruzProtocolChulakDataType.STATE_AGENT_START_EXIT: {
              await this._onAgentStartExitMessage(
                clientStateContext,
                result.message,
              );
              break;
            }
            case HoruzProtocolChulakDataType.STATE_APP_PAIRING_ENTER: {
              await this._onAppPairingEnterMessage(
                clientStateContext,
                result.message,
              );
              break;
            }
            default: {
              throw HoruzProtocolChulakErrorProtocolInvalidMessage.Create(
                'Message ' +
                  result.message.type +
                  ' not supported in AgentStart state.',
              );
            }
          }
        }
      } catch (e: any) {
        debug.trace(e?.message, e?.stack);
        this._tryHandleException(clientStateContext, e);
      }
    });
  };

  /**
   * Event
   *
   * Handle
   */
  private async _onAgentStartExitMessage(
    clientStateContext: HoruzProtocolChulakClientStateContext,
    message: HoruzProtocolChulakMessageAgentStartExitGuard,
  ) {}

  /**
   * Event
   *
   * Handle
   */
  private async _onAppPairingEnterMessage(
    clientStateContext: HoruzProtocolChulakClientStateContext,
    message: HoruzProtocolChulakMessageAppPairingEnterGuard,
  ) {
    this.$postTransition(clientStateContext, { stateNext: 'AppPairing' });
  }

  /**
   * Event
   *
   * Handle
   */
  private async _onAgentStartCompletedMessage(
    clientStateContext: HoruzProtocolChulakClientStateContext,
    message: HoruzProtocolChulakMessageAgentStartCompletedGuard,
  ) {
    await this.$executeInvocation(
      clientStateContext.contextIdentifier,
      HoruzProtocolChulakClientStateInvoker.CompletedAgentStart,
      clientStateContext,
      {} as HoruzProtocolChulakClientStateInvokerStartInput,
    );
  }

  /**
   * Event
   *
   * Handle
   */
  private async _onAppTagsRefreshed(
    clientStateContext: HoruzProtocolChulakClientStateContext,
    message: HoruzProtocolChulakMessageAppTagsRefreshedGuardInput,
  ) {
    await this.$executeInvocation(
      clientStateContext.contextIdentifier,
      HoruzProtocolChulakClientStateInvoker.RefreshedAppTags,
      clientStateContext,
      {
        tags: message.tags,
      } as HoruzProtocolChulakClientStateInvokerAppTagsRefreshedInput,
    );
  }

  /**
   * Enter state
   */
  override async $enter(
    clientStateContext: HoruzProtocolChulakClientStateContext,
  ): Promise<void> {
    // Listen for messages
    this.$root
      .$('queues')
      .$('data')
      .$on(clientStateContext.contextIdentifier, 'next', () =>
        this._onQueueNext(clientStateContext),
      );
  }

  /**
   * Exit state
   */
  override async $exit(
    clientStateContext: HoruzProtocolChulakClientStateContext,
  ): Promise<void> {
    // Stop listening for messages
    this.$root
      .$('queues')
      .$('data')
      .$removeEventListenersByNamespaceAndName(
        clientStateContext.contextIdentifier,
        'next',
      );
  }

  /**
   * Register invokers
   */
  invoker(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: typeof HoruzProtocolChulakClientStateInvoker.CompletedAgentStart,
    listener: (
      clientStateContext: HoruzProtocolChulakClientStateContext,
      input: HoruzProtocolChulakClientStateInvokerStartInput,
    ) => Promise<void>,
  ): this;
  invoker(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: typeof HoruzProtocolChulakClientStateInvoker.RefreshedAppTags,
    listener: (
      clientStateContext: HoruzProtocolChulakClientStateContext,
      input: HoruzProtocolChulakClientStateInvokerAppTagsRefreshedInput,
    ) => Promise<void>,
  ): this;
  invoker<K extends keyof typeof HoruzProtocolChulakClientStateInvoker>(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: K,
    listener: GamoraFoundationGlueListenerType,
  ): this {
    return super.$registerInvoker(contextIdentifier, name, listener);
  }
}
